/**
 * HotspotEditor.jsx — DEAD SIGNAL in-app hotspot calibration tool (dev/authoring only).
 *
 * Renders an overlay INSIDE the .cine-container-16-9 box, so its coordinate space
 * is identical to the real hotspots. Drag a box to move it, drag its corner handle
 * to resize, or use arrow keys to nudge. Because the container is always a true
 * 16:9 box that the 16:9 video fills with no crop, the percentages you set here are
 * correct at every viewport size, zoom level, portrait/landscape, and whether or not
 * the evals panel is open — calibrate once, glued everywhere.
 *
 * Edits live in localStorage (see hotspotOverrides.js) and update the real hotspots
 * in real time. When it looks right, click "Copy this scene" (or "Copy ALL") and
 * paste the result over the matching object in src/content/hotspotCoords.js, then
 * commit + redeploy. The editor never appears for a normal viewer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HOTSPOT_COORDS } from '../content/hotspotCoords.js';
import { getScene } from '../content/sceneManifest.js';
import evidenceDemo from '../content/knowledge/data/evidence-demo.json';

// Demo evidence records for the editor's reference panel. This dev tool only
// edits demo scenes in the demo build, so it reads the demo slice (not the full
// evidence-bible) to keep full-game canon out of the client bundle. Authoring-
// only fields (narrative_weight, etc.) are not shown; the D5 panel fields remain.
const EVIDENCE_BY_ID = {};
((evidenceDemo && evidenceDemo.entries) || []).forEach((e) => {
  EVIDENCE_BY_ID[e.id] = e;
  if (e.legacyId) EVIDENCE_BY_ID[e.legacyId] = e;
});
function getEvidence(id) {
  return id ? (EVIDENCE_BY_ID[id] || null) : null;
}
import {
  isHotspotEditorEnabled,
  loadOverrides,
  saveOverrides,
  clearOverrides,
  sceneOverride,
  mergedSceneCoords,
  saveToDisk,
  loadFromDisk,
} from '../systems/hotspotOverrides.js';

// Per-scene hotspot id prefix convention (see hotspotCoords.js / scene files):
// ds + scene letter + '-' + lowercase object noun, e.g. dsc-file, dsl-water, dso-chair.
const SCENE_PREFIX = {
  ds_craine_car: 'dsc',
  ds_helios_lobby: 'dsl',
  ds_helios_office: 'dso',
  ds_elevator_down: 'dse',
};
function scenePrefix(sceneId) {
  return SCENE_PREFIX[sceneId] || `ds${(sceneId || '').replace(/^ds_/, '').charAt(0) || 'x'}`;
}

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const round1 = (n) => Math.round(n * 10) / 10;
const pct = (n) => `${round1(n)}%`;
const parsePct = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** `discovers` may be a string, an array, or empty — normalize to an array. */
function discoversArray(d) {
  if (!d) return [];
  return (Array.isArray(d) ? d : [d]).map((s) => String(s).trim()).filter(Boolean);
}
/** Collapse a discovers value to undefined / string / array for storage. */
function normalizeDiscovers(d) {
  const arr = discoversArray(d);
  if (!arr.length) return undefined;
  return arr.length === 1 ? arr[0] : arr;
}

/**
 * Working box array for a scene: every committed (base) hotspot + every authored
 * target, with overridden geometry merged on top. Removed base hotspots are kept
 * in the set but flagged `removed` so they can be shown struck-through / restored.
 */
function seedBoxes(sceneId) {
  const base = HOTSPOT_COORDS[sceneId] || {};
  const ov = sceneOverride(sceneId);
  const removedSet = new Set(ov.removed || []);
  const scene = getScene(sceneId);
  const defById = {};
  ((scene && scene.hotspots) || []).forEach((h) => { defById[h.id] = h; });
  const ids = [...new Set([...Object.keys(base), ...Object.keys(ov.coords || {})])];
  return ids.map((id) => {
    const c = { ...(base[id] || {}), ...((ov.coords || {})[id] || {}) };
    const isBase = !!base[id];
    const def = defById[id] || {};
    const box = {
      id,
      left: parsePct(c.left),
      top: parsePct(c.top),
      width: parsePct(c.width),
      height: parsePct(c.height),
      cursor: c.cursor || 'pointer',
      base: isBase,
      removed: removedSet.has(id),
      type: def.type || 'examine',
    };
    if (isBase) {
      // Committed hotspot: show its scene label/comment/readout, with edits on top.
      const ed = (ov.edited || {})[id] || {};
      box._origLabel = def.label || '';
      box._origDescription = def.description || '';
      box._origReadout = (def.lumenReadout && def.lumenReadout.data) || '';
      box._readoutType = (def.lumenReadout && def.lumenReadout.type) || 'PATTERN-MATCH';
      box.label = ed.label != null ? ed.label : box._origLabel;
      box.description = ed.description != null ? ed.description : box._origDescription;
      box.readout = (ed.lumenReadout !== undefined)
        ? ((ed.lumenReadout && ed.lumenReadout.data) || '')
        : box._origReadout;
      box._origDiscovers = def.discovers;
      box.discovers = (ed.discovers !== undefined) ? ed.discovers : def.discovers;
    } else {
      const a = ov.added[id] || {};
      box.label = a.label || '';
      box.description = a.description || '';
      box._origReadout = '';
      box._readoutType = (a.lumenReadout && a.lumenReadout.type) || 'PATTERN-MATCH';
      box.readout = (a.lumenReadout && a.lumenReadout.data) || '';
      box._origDiscovers = undefined;
      box.discovers = a.discovers;
    }
    return box;
  });
}

function boxToCoord(b) {
  const c = { left: pct(b.left), top: pct(b.top), width: pct(b.width), height: pct(b.height) };
  if (b.cursor) c.cursor = b.cursor;
  return c;
}

/** Format one scene's boxes as a source-ready object literal entry. */
function formatScene(sceneId, boxes) {
  const rows = boxes
    .map((b) => {
      const cur = b.cursor ? `, cursor: '${b.cursor}'` : '';
      return `    '${b.id}': { left: '${pct(b.left)}', top: '${pct(b.top)}', width: '${pct(b.width)}', height: '${pct(b.height)}'${cur} },`;
    })
    .join('\n');
  return `  ${sceneId}: {\n${rows}\n  },`;
}

/** Escape a string for a single-quoted JS literal. */
function jsStr(s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n');
}

/**
 * Scene-file edits for this scene: hotspot objects to ADD (authored targets) and
 * committed hotspot ids to DELETE (marked removed because they are not in the
 * art). Geometry goes to hotspotCoords.js via "Copy ALL coords"; this carries
 * the scene file's hotspots[] changes (label / response / deletions).
 */
function formatSceneEdits(sceneId, boxes) {
  const added = boxes.filter((b) => !b.base && !b.removed);
  const removed = boxes.filter((b) => b.base && b.removed).map((b) => b.id);
  const modified = boxes.filter((b) => (
    b.base && !b.removed
    && ((b.label || '') !== (b._origLabel || '')
      || (b.description || '') !== (b._origDescription || '')
      || (b.readout || '') !== (b._origReadout || ''))
  ));
  const parts = [];
  if (removed.length) {
    parts.push(
      `// ${sceneId} — DELETE these from the scene file hotspots[] (object not in the art):\n`
      + removed.map((id) => `//   ${id}`).join('\n'),
    );
  }
  if (modified.length) {
    const rows = modified
      .map((b) => {
        let s = `//   ${b.id}:\n`
          + `//     label:       '${jsStr(b.label || '')}'\n`
          + `//     description: '${jsStr(b.description || '')}'`;
        if ((b.readout || '') !== (b._origReadout || '')) {
          s += `\n//     lumenReadout.data: '${jsStr(b.readout || '(cleared)')}'`;
        }
        return s;
      })
      .join('\n');
    parts.push(`// ${sceneId} — MODIFY these committed hotspots in the scene file (label / Craine comment / readout):\n${rows}`);
  }
  if (added.length) {
    const rows = added
      .map((b) => {
        let s = `    {\n`
          + `      id: '${jsStr(b.id)}',\n`
          + `      label: '${jsStr(b.label || b.id)}',\n`
          + `      type: 'examine',\n`
          + `      description: '${jsStr(b.description)}',`;
        if (b.readout && b.readout.trim()) {
          s += `\n      lumenReadout: { type: '${jsStr(b._readoutType || 'PATTERN-MATCH')}', data: '${jsStr(b.readout)}' },`;
        }
        s += `\n    },`;
        return s;
      })
      .join('\n');
    parts.push(`// ${sceneId} — ADD these to the scene file hotspots[]:\n${rows}`);
  }
  if (!parts.length) return `// ${sceneId}: no scene-file edits (no authored targets, edits, or removals).`;
  return parts.join('\n\n');
}

/** Full HOTSPOT_COORDS object with all current overrides merged in. */
function formatAll() {
  const body = Object.keys(HOTSPOT_COORDS)
    .map((sid) => {
      const merged = mergedSceneCoords(HOTSPOT_COORDS, sid);
      const rows = Object.entries(merged)
        .map(([id, c]) => {
          const cur = c.cursor ? `, cursor: '${c.cursor}'` : '';
          return `    '${id}': { left: '${c.left}', top: '${c.top}', width: '${c.width}', height: '${c.height}'${cur} },`;
        })
        .join('\n');
      return `  ${sid}: {\n${rows}\n  },`;
    })
    .join('\n');
  return `export const HOTSPOT_COORDS = {\n${body}\n};`;
}

export default function HotspotEditor({ sceneId }) {
  const enabled = isHotspotEditorEnabled();
  const [open, setOpen] = useState(false);
  const [boxes, setBoxes] = useState(() => (enabled ? seedBoxes(sceneId) : []));
  const [selectedId, setSelectedId] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [exportText, setExportText] = useState('');
  const [copyNote, setCopyNote] = useState('');
  const [idDraft, setIdDraft] = useState('');

  const overlayRef = useRef(null);
  const dragRef = useRef(null);
  const boxesRef = useRef(boxes);
  useEffect(() => { boxesRef.current = boxes; }, [boxes]);

  // Reseed when the scene changes OR the panel is opened, so the working set
  // always reflects the latest persisted overrides (edits persist immediately,
  // so reseeding never loses work).
  useEffect(() => {
    if (!enabled) return;
    setBoxes(seedBoxes(sceneId));
    setSelectedId(null);
    setExportText('');
  }, [sceneId, enabled, open]);

  // On mount: if localStorage has no work but the on-disk file does (cleared
  // storage, fresh browser, or a crash mid-session), restore from disk.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      if (Object.keys(loadOverrides()).length) return; // localStorage already has work
      const disk = await loadFromDisk();
      if (cancelled || !disk) return;
      saveOverrides(disk);
      window.dispatchEvent(new CustomEvent('ds-hotspots-changed'));
      setBoxes(seedBoxes(sceneId));
      setCopyNote('restored your mapping from disk');
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Persist the given boxes as this scene's overrides (geometry for every box +
  // definitions for authored targets) and notify the stage to re-render so the
  // real hotspots track live and authored targets hover/scan/respond.
  const persist = useCallback((bs) => {
    const all = loadOverrides();
    const coords = {};
    const added = {};
    const removed = [];
    const edited = {};
    bs.forEach((b) => {
      if (b.removed) {
        // Removed base hotspots are recorded (and lose geometry); removed
        // authored targets are simply dropped (they have no committed source).
        if (b.base) removed.push(b.id);
        return;
      }
      coords[b.id] = boxToCoord(b);
      const readoutObj = (b.readout && b.readout.trim())
        ? { type: b._readoutType || 'PATTERN-MATCH', data: b.readout }
        : null;
      const discoversVal = normalizeDiscovers(b.discovers);
      if (!b.base) {
        added[b.id] = {
          label: b.label || '',
          description: b.description || '',
          ...(readoutObj ? { lumenReadout: readoutObj } : {}),
          ...(discoversVal != null ? { discovers: discoversVal } : {}),
        };
      } else {
        const labelChanged = (b.label || '') !== (b._origLabel || '');
        const descChanged = (b.description || '') !== (b._origDescription || '');
        const readoutChanged = (b.readout || '') !== (b._origReadout || '');
        const discoversChanged = JSON.stringify(discoversVal ?? null)
          !== JSON.stringify(normalizeDiscovers(b._origDiscovers) ?? null);
        if (labelChanged || descChanged || readoutChanged || discoversChanged) {
          // Committed hotspot with an edited label / Craine comment / readout / evidence tag.
          edited[b.id] = { label: b.label || '', description: b.description || '' };
          if (readoutChanged) edited[b.id].lumenReadout = readoutObj; // null clears
          if (discoversChanged) edited[b.id].discovers = discoversVal ?? null; // null = inspect
        }
      }
    });
    all[sceneId] = { coords, added, removed, edited };
    saveOverrides(all);
    window.dispatchEvent(new CustomEvent('ds-hotspots-changed'));
  }, [sceneId]);

  const applyBoxes = useCallback((next) => {
    setBoxes(next);
    persist(next);
  }, [persist]);

  const pxToPct = useCallback((clientX, clientY) => {
    const r = overlayRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return { x: 0, y: 0 };
    return {
      x: ((clientX - r.left) / r.width) * 100,
      y: ((clientY - r.top) / r.height) * 100,
    };
  }, []);

  // Pointer drag (move / resize) bound to the window while a drag is active.
  useEffect(() => {
    if (!enabled || !open) return;
    function onMove(e) {
      const d = dragRef.current;
      if (!d) return;
      const { x, y } = pxToPct(e.clientX, e.clientY);
      const next = boxesRef.current.map((b) => {
        if (b.id !== d.id) return b;
        if (d.mode === 'move') {
          return {
            ...b,
            left: round1(clamp(d.origLeft + (x - d.startX), 0, 100)),
            top: round1(clamp(d.origTop + (y - d.startY), 0, 100)),
          };
        }
        return {
          ...b,
          width: round1(clamp(d.origWidth + (x - d.startX), 1, 100)),
          height: round1(clamp(d.origHeight + (y - d.startY), 1, 100)),
        };
      });
      // Keep the ref authoritative so onUp persists the final position even if a
      // React state flush is still pending from the last move.
      boxesRef.current = next;
      setBoxes(next);
    }
    function onUp() {
      if (dragRef.current) {
        dragRef.current = null;
        persist(boxesRef.current);
      }
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [enabled, open, pxToPct, persist]);

  // Keyboard: toggle (Shift+E), nudge selected, delete, close.
  useEffect(() => {
    if (!enabled) return;
    function onKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      const typing = tag === 'INPUT' || tag === 'TEXTAREA';
      if (e.key === 'E' && e.shiftKey && !typing) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (!open || typing) return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (!selectedId) return;
      const step = e.shiftKey ? 0.1 : 0.5;
      const resize = e.altKey || e.ctrlKey || e.metaKey;
      const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (arrows[e.key]) {
        e.preventDefault();
        const [dx, dy] = arrows[e.key];
        applyBoxes(boxesRef.current.map((b) => {
          if (b.id !== selectedId) return b;
          if (resize) {
            return {
              ...b,
              width: round1(clamp(b.width + dx * step, 1, 100)),
              height: round1(clamp(b.height + dy * step, 1, 100)),
            };
          }
          return {
            ...b,
            left: round1(clamp(b.left + dx * step, 0, 100)),
            top: round1(clamp(b.top + dy * step, 0, 100)),
          };
        }));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const b = boxesRef.current.find((x) => x.id === selectedId);
        if (b && b.base) {
          // Base hotspot: mark removed (vanishes live; export lists it to delete).
          applyBoxes(boxesRef.current.map((x) => (x.id === selectedId ? { ...x, removed: true } : x)));
        } else {
          // Authored target: drop entirely.
          applyBoxes(boxesRef.current.filter((x) => x.id !== selectedId));
          setSelectedId(null);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, open, selectedId, applyBoxes]);

  const startDrag = useCallback((e, id, mode) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(id);
    const b = boxesRef.current.find((x) => x.id === id);
    if (!b) return;
    const { x, y } = pxToPct(e.clientX, e.clientY);
    dragRef.current = {
      id, mode, startX: x, startY: y,
      origLeft: b.left, origTop: b.top, origWidth: b.width, origHeight: b.height,
    };
  }, [pxToPct]);

  const updateField = useCallback((id, field, value) => {
    const n = parsePct(value);
    const lo = 0;
    const hi = 100;
    applyBoxes(boxesRef.current.map((b) => (b.id === id ? { ...b, [field]: round1(clamp(n, field === 'width' || field === 'height' ? 1 : lo, hi)) } : b)));
  }, [applyBoxes]);

  // Label / response text for an authored target (string fields, no clamp).
  const updateMeta = useCallback((id, field, value) => {
    applyBoxes(boxesRef.current.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  }, [applyBoxes]);

  // Scan readout (PATTERN-MATCH / BIO-SCAN block) text for a hotspot.
  const updateReadout = useCallback((id, value) => {
    applyBoxes(boxesRef.current.map((b) => (b.id === id ? { ...b, readout: value } : b)));
  }, [applyBoxes]);

  // Evidence tag: comma-separated evidence ids (blank = inspect only).
  const updateDiscovers = useCallback((id, value) => {
    const arr = value.split(',').map((s) => s.trim()).filter(Boolean);
    const next = arr.length === 0 ? undefined : (arr.length === 1 ? arr[0] : arr);
    applyBoxes(boxesRef.current.map((b) => (b.id === id ? { ...b, discovers: next } : b)));
  }, [applyBoxes]);

  // Remove a box. Base hotspots are marked removed (kept for restore + export);
  // authored targets are deleted outright.
  const removeBox = useCallback((id) => {
    const b = boxesRef.current.find((x) => x.id === id);
    if (!b) return;
    if (b.base) {
      applyBoxes(boxesRef.current.map((x) => (x.id === id ? { ...x, removed: true } : x)));
    } else {
      applyBoxes(boxesRef.current.filter((x) => x.id !== id));
      setSelectedId(null);
    }
  }, [applyBoxes]);

  const restoreBox = useCallback((id) => {
    applyBoxes(boxesRef.current.map((x) => (x.id === id ? { ...x, removed: false } : x)));
  }, [applyBoxes]);

  // Rename an authored target's id (must stay unique); commits from the id field.
  const renameBox = useCallback((oldId, raw) => {
    const newId = String(raw || '').trim().replace(/\s+/g, '-');
    if (!newId || newId === oldId) return;
    const taken = new Set(boxesRef.current.map((b) => b.id).filter((x) => x !== oldId));
    if (taken.has(newId)) { setCopyNote(`id "${newId}" already exists`); return; }
    applyBoxes(boxesRef.current.map((b) => (b.id === oldId ? { ...b, id: newId } : b)));
    setSelectedId(newId);
  }, [applyBoxes]);

  const copy = useCallback(async (text, note) => {
    setExportText(text);
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch { ok = false; }
    setCopyNote(ok ? `${note} — copied to clipboard` : `${note} — select the text below & copy`);
    // eslint-disable-next-line no-console
    console.log(`[HotspotEditor] ${note}:\n${text}`);
  }, []);

  // Create a new inspection target: a box plus an empty label/response to define.
  // The id is pre-filled with the scene's prefix convention (dsc-/dsl-/dso-).
  const addBox = useCallback(() => {
    const px = scenePrefix(sceneId);
    let i = 1;
    let id = `${px}-new-${i}`;
    const ids = new Set(boxesRef.current.map((b) => b.id));
    while (ids.has(id)) { i += 1; id = `${px}-new-${i}`; }
    const box = {
      id, left: 45, top: 45, width: 10, height: 8, cursor: 'help', base: false,
      label: 'NEW TARGET', description: '',
    };
    applyBoxes([...boxesRef.current, box]);
    setSelectedId(id);
  }, [applyBoxes, sceneId]);

  const saveNow = useCallback(() => {
    saveToDisk(loadOverrides(), { immediate: true });
    setCopyNote('saved to disk ✓  →  app/.ds-hotspots.local.json');
  }, []);

  const resetScene = useCallback(() => {
    clearOverrides(sceneId);
    window.dispatchEvent(new CustomEvent('ds-hotspots-changed'));
    setBoxes(seedBoxes(sceneId));
    setSelectedId(null);
    setExportText('');
    setCopyNote('scene reset to committed coords');
  }, [sceneId]);

  const resetAll = useCallback(() => {
    clearOverrides();
    window.dispatchEvent(new CustomEvent('ds-hotspots-changed'));
    setBoxes(seedBoxes(sceneId));
    setSelectedId(null);
    setExportText('');
    setCopyNote('all scenes reset to committed coords');
  }, [sceneId]);

  const selected = useMemo(() => boxes.find((b) => b.id === selectedId) || null, [boxes, selectedId]);
  const removedIds = useMemo(() => boxes.filter((b) => b.base && b.removed).map((b) => b.id), [boxes]);

  // Seed the id draft when the selection changes (not on every geometry tick).
  useEffect(() => {
    const b = boxesRef.current.find((x) => x.id === selectedId);
    setIdDraft(b ? b.id : '');
  }, [selectedId]);

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        className={`hs-toggle ${open ? 'hs-toggle-on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="Hotspot Editor (Shift+E)"
      >
        ✎ HOTSPOTS {open ? '▾' : '▸'}
      </button>

      {open && (
        <div
          ref={overlayRef}
          className="hs-editor-overlay"
          onPointerDown={() => setSelectedId(null)}
        >
          {showGrid && <div className="hs-grid" aria-hidden />}
          {boxes.filter((b) => !b.removed).map((b) => (
            <div
              key={b.id}
              className={`hs-box ${b.id === selectedId ? 'sel' : ''} ${b.base ? '' : 'hs-box-new'}`}
              style={{ left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` }}
              onPointerDown={(e) => startDrag(e, b.id, 'move')}
            >
              <span className="hs-box-label">{b.id}</span>
              <span
                className="hs-handle"
                onPointerDown={(e) => startDrag(e, b.id, 'resize')}
              />
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="hs-panel" onPointerDown={(e) => e.stopPropagation()}>
          <div className="hs-panel-head">
            <strong>HOTSPOT EDITOR</strong>
            <button type="button" className="hs-x" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="hs-scene">scene: <code>{sceneId}</code> · {boxes.length} boxes</div>

          <div className="hs-list">
            {boxes.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`hs-li ${b.id === selectedId ? 'sel' : ''} ${b.removed ? 'hs-li-removed' : ''}`}
                onClick={() => setSelectedId(b.id)}
                title={b.removed ? 'marked removed — select to restore' : undefined}
              >
                {b.id}
              </button>
            ))}
          </div>

          {selected ? (
            <div className="hs-fields">
              {selected.base ? (
                <div className="hs-field-id">
                  {selected.id}
                  {((selected.label || '') !== (selected._origLabel || '')
                    || (selected.description || '') !== (selected._origDescription || '')) ? ' · edited' : ''}
                </div>
              ) : (
                <div className="hs-added">
                  <div className="hs-field-id">AUTHORED TARGET</div>
                  <label className="hs-field hs-field-text">
                    <span>id</span>
                    <input
                      type="text"
                      value={idDraft}
                      onChange={(e) => setIdDraft(e.target.value)}
                      onBlur={() => renameBox(selected.id, idDraft)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); renameBox(selected.id, idDraft); } }}
                    />
                  </label>
                  <div className="hs-id-hint">
                    convention: <code>{scenePrefix(sceneId)}-</code> + object, lowercase &amp; unique
                    (e.g. <code>{scenePrefix(sceneId)}-bookshelf</code>). Enter to commit.
                  </div>
                </div>
              )}
              <label className="hs-field hs-field-text">
                <span>label</span>
                <input
                  type="text"
                  value={selected.label || ''}
                  onChange={(e) => updateMeta(selected.id, 'label', e.target.value)}
                />
              </label>

              {(() => {
                const ids = discoversArray(selected.discovers);
                const isEvidence = ids.length > 0;
                return (
                  <div className={`hs-evidence ${isEvidence ? 'is-ev' : 'is-inspect'}`}>
                    <div className="hs-ev-badge">{isEvidence ? '◆ EVIDENCE' : '◇ INSPECT — no evidence'}</div>
                    <label className="hs-field hs-field-text">
                      <span>evidence</span>
                      <input
                        type="text"
                        value={ids.join(', ')}
                        placeholder="evidence id(s), comma-sep — blank = inspect only"
                        onChange={(e) => updateDiscovers(selected.id, e.target.value)}
                      />
                    </label>
                    {ids.map((id) => {
                      const ev = getEvidence(id);
                      if (!ev) {
                        return <div key={id} className="hs-ev-unknown">⚠ {id} — not in evidence bible</div>;
                      }
                      return (
                        <div key={id} className="hs-ev-card">
                          <div className="hs-ev-title">{ev.title || id} <span className="hs-ev-id">{id}</span></div>
                          {ev.description && <div className="hs-ev-row"><b>Found:</b> {ev.description}</div>}
                          {ev.narrative_weight && <div className="hs-ev-row"><b>Why it matters:</b> {ev.narrative_weight}</div>}
                          {Array.isArray(ev.connections) && ev.connections.length > 0 && (
                            <div className="hs-ev-row"><b>Connects to:</b> {ev.connections.join(', ')}</div>
                          )}
                          {ev.bioscanTopic && <div className="hs-ev-row"><b>Bio-scan topic:</b> {ev.bioscanTopic}</div>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <label className="hs-field-area">
                <span>response (Craine)</span>
                <textarea
                  rows={3}
                  value={selected.description || ''}
                  placeholder="What Craine says about this. (Craine is the detective; you are Chip.)"
                  onChange={(e) => updateMeta(selected.id, 'description', e.target.value)}
                />
              </label>
              <label className="hs-field-area">
                <span>scan readout ({selected._readoutType || 'PATTERN-MATCH'})</span>
                <textarea
                  rows={3}
                  value={selected.readout || ''}
                  placeholder="Optional PATTERN-MATCH / BIO-SCAN block. Leave blank for none."
                  onChange={(e) => updateReadout(selected.id, e.target.value)}
                />
              </label>
              {['left', 'top', 'width', 'height'].map((f) => (
                <label key={f} className="hs-field">
                  <span>{f}</span>
                  <input
                    type="number"
                    step="0.1"
                    value={selected[f]}
                    onChange={(e) => updateField(selected.id, f, e.target.value)}
                  />
                  <span className="hs-unit">%</span>
                </label>
              ))}
              {selected.removed ? (
                <button type="button" className="hs-restore" onClick={() => restoreBox(selected.id)}>
                  ↺ Restore hotspot
                </button>
              ) : selected.base ? (
                <button type="button" className="hs-remove" onClick={() => removeBox(selected.id)}>
                  ⊘ Remove (not in art)
                </button>
              ) : (
                <button type="button" className="hs-remove" onClick={() => removeBox(selected.id)}>
                  ✕ Delete target
                </button>
              )}
            </div>
          ) : (
            <div className="hs-hint">click a box or list item to select</div>
          )}

          {removedIds.length > 0 && (
            <div className="hs-removed-note">
              ⊘ removed (also delete from the scene file): {removedIds.join(', ')}
            </div>
          )}

          <div className="hs-help">
            drag = move · corner = resize · arrows = nudge 0.5% · shift+arrows = 0.1% · alt+arrows = resize · del = remove (a committed hotspot is marked removed and vanishes live; select it in the list to restore). Edit any hotspot's <strong>label</strong> or <strong>response (Craine)</strong> above — committed edits apply live and are listed under <strong>Copy scene edits</strong>. <strong>+ target</strong> adds an inspection hotspot you name and write a response for; it goes live immediately (cyan).
          </div>

          <label className="hs-check">
            <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
            show grid
          </label>

          <button type="button" className="hs-save" onClick={saveNow}>💾 Save all to disk</button>
          <div className="hs-saved-hint">autosaves on every change → <code>app/.ds-hotspots.local.json</code></div>

          <div className="hs-btns">
            <button type="button" onClick={addBox}>+ target</button>
            <button type="button" onClick={() => copy(formatAll(), 'ALL coords')} title="All hotspot geometry → paste into hotspotCoords.js">Copy ALL coords</button>
            <button type="button" onClick={() => copy(formatSceneEdits(sceneId, boxes), 'scene edits')} title="Scene file hotspots[] changes: targets to add + hotspots to delete">Copy scene edits</button>
            <button type="button" onClick={() => copy(formatScene(sceneId, boxes), 'this scene coords')}>Copy scene coords</button>
            <button type="button" onClick={resetScene}>Reset scene</button>
            <button type="button" className="hs-danger" onClick={resetAll}>Reset all</button>
          </div>

          {copyNote && <div className="hs-note">{copyNote}</div>}
          {exportText && (
            <textarea className="hs-export" readOnly value={exportText} onFocus={(e) => e.target.select()} />
          )}
        </div>
      )}
    </>
  );
}
